import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarIcon,
  CalendarDaysIcon,
  PlusCircleIcon,
  SunIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import { TWclassNames } from "../../utils/Div";

const labelClass =
  "max-w-full truncate px-0.5 text-center text-[10px] font-medium leading-tight";

const itemClass = (active, primary) =>
  TWclassNames(
    "flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 transition-colors",
    primary
      ? "text-blue-600 hover:bg-blue-50 active:bg-blue-100"
      : active
        ? "bg-blue-50 text-blue-600 active:bg-blue-100"
        : "text-gray-600 hover:bg-gray-50 active:bg-gray-100"
  );

/**
 * Fixed bottom navigation for the hours page on mobile.
 * Hidden from sm+ (tablet/desktop use header controls in HoursViewComponent).
 */
export function HoursMobileActionsNav({
  currentView = "day",
  onViewChange,
  onGoToToday,
  onAddHour,
}) {
  const { t } = useTranslation();

  const navItems = useMemo(
    () => [
      {
        key: "today",
        label: t("Today"),
        icon: CalendarIcon,
        onClick: onGoToToday,
        active: false,
      },
      {
        key: "day",
        label: t("Day"),
        icon: SunIcon,
        onClick: () => onViewChange?.("day"),
        active: currentView === "day",
      },
      {
        key: "week",
        label: t("Week"),
        icon: CalendarDaysIcon,
        onClick: () => onViewChange?.("week"),
        active: currentView === "week",
      },
      {
        key: "period",
        label: t("Period"),
        icon: TableCellsIcon,
        onClick: () => onViewChange?.("period"),
        active: currentView === "period",
      },
      {
        key: "add",
        label: t("hours.sheet.add_hour"),
        icon: PlusCircleIcon,
        onClick: onAddHour,
        active: false,
        primary: true,
      },
    ],
    [t, currentView, onViewChange, onGoToToday, onAddHour]
  );

  return (
    <div
      className="sm:hidden pointer-events-none fixed bottom-0 left-0 right-0 z-50"
      style={{ paddingBottom: "max(0.35rem, env(safe-area-inset-bottom, 0px))" }}
    >
      <nav
        className="pointer-events-auto border-t border-gray-200 bg-white/95 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] backdrop-blur-sm supports-[backdrop-filter]:bg-white/90"
        aria-label={t("hours.mobile.navAriaLabel")}
      >
        <div className="mx-auto grid max-w-lg grid-cols-5 items-stretch gap-0 px-0.5 pb-1 pt-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={item.onClick}
                className={itemClass(item.active, item.primary)}
                title={item.label}
              >
                <Icon
                  className={TWclassNames(
                    "shrink-0",
                    item.primary ? "h-6 w-6" : "h-5 w-5"
                  )}
                  aria-hidden
                />
                <span className={labelClass}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default HoursMobileActionsNav;
